// Work Order data model + shared scheduling-conflict detection.
// §2: a WO cannot exist without a start AND end date — there is no
// "unscheduled WO" state. §1.7/§4.7: conflict warnings are inline and
// NON-BLOCKING — warn, never prevent.

import { dealerToday } from './dealerDate';

/** Default times used when Settings hasn't been loaded yet. */
export const DEFAULT_WO_TIMES = { startTime: '08:00', endTime: '17:00' };

function todayIso() {
  return dealerToday(); // IDI-615 D2
}

/**
 * Normalize a WO payload so it always carries a valid range.
 * End defaults to same-day as start. Times fall back to settings/defaults.
 */
export function normalizeWorkOrder(wo = {}, settingsDefaults = {}) {
  const defaults = { ...DEFAULT_WO_TIMES, ...(settingsDefaults || {}) };
  const startDate = wo.startDate || wo.start_date || todayIso();
  const endDate = wo.endDate || wo.end_date || startDate;
  return {
    ...wo,
    startDate,
    endDate: endDate < startDate ? startDate : endDate,
    startTime: wo.startTime || wo.start_time || defaults.startTime,
    endTime: wo.endTime || wo.end_time || defaults.endTime,
  };
}

/** A WO is valid to persist only when it has a start AND end date. */
export function workOrderHasValidRange(wo = {}) {
  const start = wo.startDate || wo.start_date;
  const end = wo.endDate || wo.end_date;
  if (!start || !end) return false;
  return end >= start;
}

function rangesOverlap(aStart, aEnd, bStart, bEnd) {
  return aStart <= bEnd && bStart <= aEnd;
}

/**
 * Detect scheduling conflicts for a proposed assignment.
 * Shared by the WO assignment flow (§1.7) and the Calendar (§4.7).
 *
 * @param {Object} proposed - { startDate, endDate, techIds, crewIds, resourceIds }
 * @param {Object} ctx
 *   ctx.existing      - array of scheduled items { id, title, startDate, endDate, techIds, crewIds, resourceIds }
 *   ctx.pto           - array of PTO appointments { techId, startDate, endDate }
 * @returns {Array} warnings: { type, message, conflictId? } — never blocking
 */
export function detectConflicts(proposed = {}, ctx = {}) {
  const warnings = [];
  const { startDate, endDate } = normalizeWorkOrder(proposed);
  const techIds = proposed.techIds || [];
  const crewIds = proposed.crewIds || [];
  const resourceIds = proposed.resourceIds || [];
  const existing = ctx.existing || [];
  const pto = ctx.pto || [];

  // Tech on PTO
  pto.forEach((p) => {
    if (techIds.includes(p.techId) && rangesOverlap(startDate, endDate, p.startDate, p.endDate)) {
      warnings.push({
        type: 'tech_pto',
        message: `${p.techName || 'This tech'} has time off ${p.startDate}–${p.endDate}.`,
        conflictId: p.id,
      });
    }
  });

  // Double-booking of techs / crews / resources
  existing.forEach((e) => {
    if (proposed.id && e.id === proposed.id) return;
    if (!rangesOverlap(startDate, endDate, e.startDate, e.endDate)) return;
    const techClash = (e.techIds || []).some((t) => techIds.includes(t));
    const crewClash = (e.crewIds || []).some((c) => crewIds.includes(c));
    const resClash = (e.resourceIds || []).some((r) => resourceIds.includes(r));
    if (techClash) {
      warnings.push({ type: 'tech_double_booked', message: `Tech is already assigned to "${e.title}" on these dates.`, conflictId: e.id });
    }
    if (crewClash) {
      warnings.push({ type: 'crew_double_booked', message: `Crew is already booked on "${e.title}".`, conflictId: e.id });
    }
    if (resClash) {
      warnings.push({ type: 'resource_double_booked', message: `Resource is already in use on "${e.title}".`, conflictId: e.id });
    }
  });

  return warnings;
}
